/**
 * „Przypisz ankietę" for one client — a check-in from the trainer's list, or a new one.
 *
 * Built 8 October 2026 (Trello „Nie da się przypisać ankiety będąc w kliencie", owner-approved).
 * The shell is the client card's own settings dialog's (`shared/ui/formDialog` on `dialogSurface`,
 * rows from `shared/ui/choiceRow`).
 *
 * ── Several check-ins at once (owner, 9 October 2026) ────────────────────────────────────────
 * Every check-in is a native checkbox, and „Przypisz (N)" sends one write per tick. A row the
 * backend would refuse stays in the list, `aria-disabled` with its reason, and the press is refused
 * in the handler — a hidden row would leave the trainer wondering where their form went.
 * „Utwórz nową ankietę" is an act above the list, not an option in it.
 *
 * ── The list holds its box in every state ────────────────────────────────────────────────────
 * Five rows' room from the first frame, scrolled inside past that, so the answer landing changes
 * ink and never geometry; the wait forks on the gate, the failure inside the not-waiting arm.
 */

import { Dialog } from '@base-ui/react/dialog';
import { type QueryKey } from '@tanstack/react-query';
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckIcon, ChevronRightIcon } from '~/shared/ui/AuthIcons';
import choice from '~/shared/ui/choiceRow.module.css';
import surface from '~/shared/ui/dialogSurface.module.css';
import { EmptyState } from '~/shared/ui/EmptyState';
import { InboxMarkIcon } from '~/shared/ui/EmptyStateIcons';
import form from '~/shared/ui/formDialog.module.css';
import { CloseGlyph } from '~/shared/ui/FormGlyphs';
import skeleton from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import { useAssignSurvey } from '../lib/useAssignSurvey';
import {
  assignedSentence,
  assignLabel,
  failureSentence,
  type SurveyChoiceRow,
} from '../model/surveyChoices';

import styles from './AssignSurveyDialog.module.css';

const WORDS = 'web:assignSurvey';

/** How many skeleton rows stand in the reserve. The reserve itself is five rows tall. */
const SKELETON_ROWS = [0, 1, 2] as const;

/** Resolved at close time: `true` keeps Base UI's own return, an element replaces it. */
type FinalFocus = (() => HTMLElement | boolean | null) | undefined;

export interface AssignSurveyDialogProps {
  readonly clientId: number;
  readonly clientName: string;
  readonly open: boolean;
  readonly onClose: () => void;
  /** „Utwórz nową ankietę" pressed: the host closes the dialog and opens the editor for this client. */
  readonly onCreateNew: () => void;
  /** The host's one live region. The dialog closes on the assignment it announces. */
  readonly announce: (message: string) => void;
  /** Reads the assignment changes beyond the templates list — the client's survey history. */
  readonly invalidates: readonly QueryKey[];
  /** Where focus goes on close; resolved at close time, because the opener may be gone by then. */
  readonly finalFocus?: FinalFocus;
}

export function AssignSurveyDialog({
  clientId,
  clientName,
  open,
  onClose,
  onCreateNew,
  announce,
  invalidates,
  finalFocus,
}: AssignSurveyDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={surface.backdrop} />
        <AssignSurveyPopup
          clientId={clientId}
          clientName={clientName}
          invalidates={invalidates}
          finalFocus={finalFocus}
          onClose={onClose}
          onCreateNew={onCreateNew}
          onAssigned={(surveys) => {
            announce(assignedSentence(clientName, surveys, t));
            onClose();
          }}
        />
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The popup, and with it the read: mounted only while the dialog is open. */
function AssignSurveyPopup({
  clientId,
  clientName,
  invalidates,
  finalFocus,
  onClose,
  onCreateNew,
  onAssigned,
}: {
  readonly clientId: number;
  readonly clientName: string;
  readonly invalidates: readonly QueryKey[];
  readonly finalFocus: FinalFocus;
  readonly onClose: () => void;
  readonly onCreateNew: () => void;
  readonly onAssigned: (surveyTitles: readonly string[]) => void;
}) {
  const { t, i18n } = useTranslation();
  const flow = useAssignSurvey(clientId, invalidates);
  const createRef = useRef<HTMLButtonElement | null>(null);
  const reasonId = useId();
  const { content, gate } = flow;
  const refused = flow.count === 0;
  const failure = flow.failure === null ? null : failureSentence(flow.failure, t, i18n.language);

  return (
    <Dialog.Popup
      className={`${String(surface.popup)} ${String(form.dialog)}`}
      initialFocus={createRef}
      finalFocus={finalFocus}
      aria-busy={gate.waiting}
    >
      <div className={form.dialogHead}>
        <div>
          <span className={form.eyebrow}>{clientName}</span>
          <Dialog.Title className={form.dialogTitle}>{t(`${WORDS}.title`)}</Dialog.Title>
        </div>
        <Dialog.Close className={form.close} aria-label={t(`${WORDS}.close`)}>
          <CloseGlyph className={form.closeGlyph} />
        </Dialog.Close>
      </div>

      {/* An act, not an option: it leaves for the editor at once, so it cannot mix with the ticks. */}
      <button
        ref={createRef}
        type="button"
        className={`${String(choice.row)} ${String(styles.create)}`}
        aria-disabled={flow.assigning ? true : undefined}
        onClick={() => {
          if (!flow.assigning) onCreateNew();
        }}
      >
        <span className={choice.rowText}>
          <span className={choice.rowTitle}>{t(`${WORDS}.createTitle`)}</span>
          <span className={choice.rowMeta}>{t(`${WORDS}.createCaption`)}</span>
        </span>
        <ChevronRightIcon className={styles.createGlyph} />
      </button>

      <fieldset className={styles.set}>
        <legend className={`${String(form.eyebrow)} ${String(styles.divider)}`}>
          {t(`${WORDS}.divider`)}
        </legend>

        {/* The reserve. Five rows' room in every state; past that the rows scroll inside it. */}
        <div className={styles.list} aria-busy={gate.waiting ? 'true' : undefined}>
          {content === 'waiting' ? (
            <div className={choice.rows} aria-hidden="true">
              {SKELETON_ROWS.map((row) => (
                <div key={row} className={choice.row}>
                  {gate.skeleton ? (
                    <>
                      <i className={`${String(skeleton.bar)} ${String(styles.barMark)}`} />
                      <i className={`${String(skeleton.bar)} ${String(styles.barText)}`} />
                    </>
                  ) : null}
                </div>
              ))}
              <span className={hidden.srOnly}>{t(`${WORDS}.loading`)}</span>
            </div>
          ) : content === 'failed' ? (
            <div className={`${String(styles.state)} ${gate.reveal}`} role="alert">
              <span>{t(`${WORDS}.failed`)}</span>
              <button type="button" className={surface.ghost} onClick={flow.retry}>
                {t(`${WORDS}.retry`)}
              </button>
            </div>
          ) : content === 'empty' ? (
            <div className={`${String(styles.state)} ${gate.reveal}`}>
              <EmptyState mark={<InboxMarkIcon />} tone="action" title={t(`${WORDS}.empty`)} />
            </div>
          ) : (
            <div className={`${String(choice.rows)} ${gate.reveal}`}>
              {flow.rows.map((row) => (
                <SurveyRow
                  key={row.id}
                  row={row}
                  ticked={row.state === 'open' && flow.selected.has(row.id)}
                  locked={flow.assigning}
                  onToggle={() => {
                    flow.toggle(row.id);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </fieldset>

      {failure === null ? null : (
        <p className={form.refusal} role="alert">
          {failure}
        </p>
      )}

      <div className={form.dialogActs}>
        {refused ? (
          <span id={reasonId} className={form.actReason}>
            {t(`${WORDS}.pickFirst`)}
          </span>
        ) : null}
        <button type="button" className={surface.ghost} onClick={onClose}>
          {t(`${WORDS}.cancel`)}
        </button>
        <button
          type="button"
          className={surface.primary}
          aria-disabled={refused || flow.assigning ? true : undefined}
          aria-describedby={refused ? reasonId : undefined}
          aria-busy={flow.assigning}
          onClick={() => {
            if (refused || flow.assigning) return;
            flow.assign(onAssigned);
          }}
        >
          {assignLabel(flow.count, flow.assigning, t)}
        </button>
      </div>
    </Dialog.Popup>
  );
}

/** One check-in as a checkbox; a refused one says why in its badge and its description. */
function SurveyRow({
  row,
  ticked,
  locked,
  onToggle,
}: {
  readonly row: SurveyChoiceRow;
  readonly ticked: boolean;
  readonly locked: boolean;
  readonly onToggle: () => void;
}) {
  const { t } = useTranslation();
  const reasonId = useId();
  const refused = row.state !== 'open';
  return (
    <label
      className={`${String(choice.row)} ${ticked ? String(choice.rowSelected) : ''} ${
        refused ? String(styles.refused) : ''
      }`}
    >
      <input
        className={hiddenControl.control}
        type="checkbox"
        value={row.id}
        checked={ticked}
        disabled={locked}
        aria-disabled={refused ? 'true' : undefined}
        aria-describedby={refused ? reasonId : undefined}
        onChange={() => {
          if (!refused) onToggle();
        }}
      />
      <span
        className={`${String(choice.rowMark)} ${String(styles.tick)} ${ticked ? String(styles.tickOn) : ''}`}
        aria-hidden="true"
      >
        {ticked ? <CheckIcon className={styles.tickGlyph} /> : null}
      </span>
      <span className={choice.rowText}>
        <span className={refused ? styles.refusedTitle : choice.rowTitle}>{row.title}</span>
        {row.cadence === null ? null : <span className={choice.rowMeta}>{row.cadence}</span>}
      </span>
      {refused ? (
        <>
          <span className={choice.rowBadge}>{t(`${WORDS}.state.${row.state}`)}</span>
          <span id={reasonId} className={hidden.srOnly}>
            {t(`${WORDS}.reason.${row.state}`)}
          </span>
        </>
      ) : null}
    </label>
  );
}
